import {alpha, Typography, Stack} from '@mui/material'
import AppTextField from "@/components/AppTextField";
import FormItem from "@/components/FormItem";
import NationAutoComplete from "@/components/NationAutoComplete";
import TextFieldLimitLabel from "@/components/TextFieldLimitLabel";
import AppLoadingButton from "@/components/mui/LoadingButton";
import API from "@/constant/API";
import {getBillAddress} from "@/redux/slices/card";
import {dispatch} from "@/redux/store";
import axiosInstance from "@/utils/axios";
import {Box, Grid} from "@mui/material";
import {useFormik} from "formik";
import lodash from "lodash";
import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";

import * as Yup from "yup";
import NationSelect from "@/components/NationSelect";
import {useBoolean} from "ahooks";

function BillAddressForm({cardId, onSuccess, onBeforeSubmit, onAfterSubmit, onCancel}) {
  const {t} = useTranslation();
  const [loading, setLoading] = useState(false);
  const [country, setCountry] = useState(null);

  const {countries} = useSelector((state) => state.app);
  const {billAddress} = useSelector((state) => state.card);
  const [nationSelectModalShow, nationSelectModalShowOperate] = useBoolean(false)

  useEffect(() => {
    _initData();
  }, []);

  const _initData = () => {
    dispatch(getBillAddress(cardId));
    // if (!billAddress) {
    //   dispatch(getBillAddress(cardId));
    // }
  }

  useEffect(() => {
    if (billAddress) {
      if (billAddress.country) {
        _onCountryChange(
          countries.find((country) => country.code3 === billAddress.country)
        );
      }
      setValues(billAddress);
      // validate form on initial values set
      setTimeout(() => validateForm(billAddress), 0);
    }
  }, [billAddress]);

  let yupShape = {
    preferredCardName: Yup.string().trim().required("this_field_is_required"),
    line1: Yup.string().trim().required("this_field_is_required")
      .test('line1', t('invalid_characters'), (value) => {
        const isValid = /^[\s\w\"'’#$&()*+,-./=]+$/.test(value);
        return isValid;
      }),
      // matches(/^[\\s\\w\"'’#$&()*+,-./=]+$/g, t('invalid_characters')),
    line2: Yup.string().trim().required("this_field_is_required")
      .test('line1', t('invalid_characters'), (value) => {
        const isValid = /^[\s\w\"'’#$&()*+,-./=]+$/.test(value);
        return isValid;
      }),
    city: Yup.string().trim().required("this_field_is_required"),
    state: Yup.string().trim().required("this_field_is_required"),
    postalCode: Yup.string().trim().required("this_field_is_required"),
  };
  const validationSchema = Yup.object().shape(yupShape);

  const _onSave = async () => {
    if (!country?.code3) {
      // todo: 没选择国家（由于有默认选中项，理论不存在），需要提示用户
      return;
    }

    setLoading(true);
    let params = {...values, country: country?.code3};
    if (cardId) {
      params = {...params, cardId}
    }
    try {
      await axiosInstance.post(API.BILL_ADDRESS_SAVE, params);
      _onSuccess();
    } finally {
      setLoading(false);
    }
  };

  const beforeSubmit = () => {
    onBeforeSubmit && onBeforeSubmit();
  }

  const _onSuccess = () => {
    if (onSuccess) onSuccess();
  };

  const {
    errors,
    values,
    touched,
    setValues,
    setFieldValue,
    handleChange,
    handleBlur,
    handleSubmit,
    validateForm,
  } = useFormik({
    initialValues: {
      preferredCardName: "",
      line1: "",
      line2: "",
      city: "",
      state: "",
      postalCode: "",
    },
    validationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    validateOnMount: true,
    onSubmit: _onSave,
  });

  const _onCountryChange = (record) => {
    setCountry(record);
  };

  const isFormValid = lodash.isEmpty(errors);

  const nameHandleInput = (e) => {
    e.target.value = e.target.value.replace(/[^a-zA-Z\s]/g, '');
  };
  const addressHandleInput = (e) => {
    e.target.value = e.target.value.replace(/[^\x00-\x7F]/g, '');
  };

  return (
    <>
      <Box>
        <form className="form" onSubmit={handleSubmit}>
          <Stack className="form-title-box" justifyContent="center" sx={{marginBottom:'24px'}}>
            {/*<div className="form-title">{t("enter_your_billing_address")}</div>*/}
            <Stack direction="row" alignItems="center"
                   sx={theme => {
                     return {
                       backgroundColor: alpha(theme.palette.warning.main, 0.4),
                       padding: '8px 12px',
                     }
                   }}
            >
              <img src="/images/exclamation.svg"/>
              <Typography sx={theme => {
                return {fontSize: theme.typography.pxToRem(12)};
              }} color="warning.dark" className="form-subtitle">
                {t("please_fill_in_the_information_in_english")}
              </Typography>
            </Stack>
          </Stack>

          <Stack direction={'column'} gap={'24px'}>
            <Box>
              <AppTextField
                  label={t("card_holder_name")}
                  fullWidth
                  autoComplete="off"
                  name="preferredCardName"
                  size="large"
                  clearable
                  variant="outlined"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  onClear={() => setFieldValue("preferredCardName", "")}
                  inputProps={{
                    maxLength: 27,
                    onInput: nameHandleInput
                  }}
                  endAdornment={
                    <TextFieldLimitLabel
                        current={values.preferredCardName.length}
                        total={27}
                    />
                  }
                  error={Boolean(
                      touched.preferredCardName && errors.preferredCardName
                  )}
                  helperText={
                      touched.preferredCardName && t(errors.preferredCardName)
                  }
                  value={values.preferredCardName}
              />
            </Box>
            <Box>
              <FormItem label={t("country_region")}>
                <NationSelect showAreaCode={false} selected={country} onSelect={_onCountryChange} />
              </FormItem>
            </Box>
            <Box>
              <AppTextField
                label={t("address_line1")}
                fullWidth
                autoComplete="off"
                name="line1"
                size="large"
                clearable
                variant="outlined"
                onChange={(e) => {
                  let isValid = true
                  if (e.target.value.length > 0) {
                    isValid = /^[\s\w\"'’#$&()*+,-./=]+$/.test(e.target.value);
                  }
                  if (!isValid) {
                    return
                  }
                  setFieldValue('line1', e.target.value);
                }}
                onBlur={handleBlur}
                onClear={() => setFieldValue("line1", "")}
                error={Boolean(touched.line1 && errors.line1)}
                helperText={touched.line1 && t(errors.line1)}
                inputProps={{
                  maxLength: 50,
                  onInput:addressHandleInput
                }}
                endAdornment={
                  <TextFieldLimitLabel
                    current={values.line1.length}
                    total={50}
                  />
                }
                value={values.line1}
              />

            </Box>
            <Box>
              <AppTextField
                label={t("address_line2")}
                fullWidth
                autoComplete="off"
                name="line2"
                size="large"
                clearable
                variant="outlined"
                // onChange={handleChange}
                onChange={(e) => {
                  let isValid = true
                  if (e.target.value.length > 0) {
                    isValid = /^[\s\w\"'’#$&()*+,-./=]+$/.test(e.target.value);
                  }
                  if (!isValid) {
                    return
                  }
                  setFieldValue('line2', e.target.value);
                }}
                onBlur={handleBlur}
                onClear={() => setFieldValue("line2", "")}
                error={Boolean(touched.line2 && errors.line2)}
                helperText={touched.line2 && t(errors.line2)}
                inputProps={{
                  maxLength: 50,
                  onInput:addressHandleInput
                }}
                endAdornment={
                  <TextFieldLimitLabel
                    current={values.line2.length}
                    total={50}
                  />
                }
                value={values.line2}
              />
            </Box>
            <Box>
              <AppTextField
                  label={t("province_state")}
                  fullWidth
                  autoComplete="off"
                  name="state"
                  size="large"
                  clearable
                  variant="outlined"
                  onChange={(e) => {
                    let isValid = true
                    if (e.target.value.length > 0) {
                      isValid = /^[\s\w\"'’#$&()*+,-./=]+$/.test(e.target.value);
                    }
                    if (!isValid) {
                      return
                    }
                    setFieldValue('state', e.target.value);
                  }}
                  onBlur={handleBlur}
                  onClear={() => setFieldValue("state", "")}
                  error={Boolean(touched.state && errors.state)}
                  helperText={touched.state && t(errors.state)}
                  inputProps={{
                    maxLength: 20,
                    onInput:addressHandleInput
                  }}
                  endAdornment={
                    <TextFieldLimitLabel
                        current={values.state.length}
                        total={20}
                    />
                  }
                  value={values.state}
              />
            </Box>
            <Box>
              <AppTextField
                label={t("city")}
                fullWidth
                autoComplete="off"
                name="city"
                size="large"
                clearable
                variant="outlined"
                onChange={(e) => {
                  let isValid = true
                  if (e.target.value.length > 0) {
                    isValid = /^[\s\w\"'’#$&()*+,-./=]+$/.test(e.target.value);
                  }
                  if (!isValid) {
                    return
                  }
                  setFieldValue('city', e.target.value);
                }}
                onBlur={handleBlur}
                onClear={() => setFieldValue("city", "")}
                error={Boolean(touched.city && errors.city)}
                helperText={touched.city && t(errors.city)}
                inputProps={{
                  maxLength: 20,
                  onInput:addressHandleInput
                }}
                endAdornment={
                  <TextFieldLimitLabel
                    current={values.city.length}
                    total={20}
                  />
                }
                value={values.city}
              />
            </Box>
            <Box>
              <AppTextField
                label={t("postal_code")}
                fullWidth
                autoComplete="off"
                name="postalCode"
                size="large"
                clearable
                variant="outlined"
                // onChange={handleChange}
                onChange={(e) => {
                  let isValid = true
                  if (e.target.value.length > 0) {
                    isValid = /^[a-zA-Z0-9-\\s]+$/.test(e.target.value);
                  }
                  if (!isValid) {
                    return
                  }
                  setFieldValue('postalCode', e.target.value);
                }}
                onBlur={handleBlur}
                onClear={() => setFieldValue("postalCode", "")}
                error={Boolean(touched.postalCode && errors.postalCode)}
                helperText={touched.postalCode && t(errors.postalCode)}
                inputProps={{
                  maxLength: 10,
                  onInput:addressHandleInput
                }}
                endAdornment={
                  <TextFieldLimitLabel
                    current={values.postalCode.length}
                    total={10}
                  />
                }
                value={values.postalCode}
              />
            </Box>
            <Box>
              <AppLoadingButton
                  type="submit"
                  disabled={!isFormValid}
                  fullWidth
                  loading={loading}
                  size="large"
                  variant="contained"
              >
                {t("save")}
              </AppLoadingButton>
            </Box>
          </Stack>
        </form>
      </Box>
    </>
  );
}

export default BillAddressForm;
